import { getErrorMessage } from 'utils';
import { notification } from 'antd';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getProfile, getFunctions, logout as logoutRequest } from 'services/auth.service';
import {
  getStoredNavigationLayout,
  normalizeNavigationLayout,
  persistNavigationLayout,
} from 'layouts/navigationLayout';
import { PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { ensureAuthenticatedPortalScope } from 'utils/portalScopeBootstrap';
import { unregisterNotificationDevice } from 'services/notification.service';
import { NOTIFICATION_DEVICE_STORAGE_KEY, NOTIFICATION_LOGOUT_EVENT, NOTIFICATION_LOGOUT_CANCELLED_EVENT } from 'utils/notificationPush';

export const isAdministratorUser = (profileData) => {
  return Number(profileData?.isAdministrator) === 1;
};

export const normalizeAuthUser = (profileData) => {
  if (!profileData) return profileData;

  return {
    ...profileData,
    isAdministrator: isAdministratorUser(profileData) ? 1 : 0,
  };
};

const AuthContext = createContext({
  initializing: false,
  user: null,
  error: null,
  setting: {
    navTheme: 'dark',
    layout: 'sidemenu',
    fixedHeader: true,
    navigationLayout: 'auto',
  },
  setSetting: () => {},
  setUser: () => {},
  retryInitialization: () => {},
  logout: () => {},
  collapsed: false,
  setCollapsed: () => {},
});
AuthContext.displayName = 'AuthContext';

const createInitialSetting = () => ({
  navTheme: 'dark',
  layout: 'sidemenu',
  fixedHeader: true,
  navigationLayout: getStoredNavigationLayout(),
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const permissionSnapshot = useMemo(() => ({
    source: user?.functions,
    keys: new Set((user?.functions ?? []).map(value => `${value || ''}`.trim().toUpperCase()).filter(Boolean)),
  }), [user?.functions]);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [setting, setSettingState] = useState(createInitialSetting);
  const [collapsed, setCollapsed] = useState(false);
  const scopePermissionRequestId = useRef(0);

  const [isUserShift, setIsUserShift] = useState(false);

  useEffect(() => {
    const handleExpiredSession = () => {
      setError(null);
      setUser(null);
    };
    window.addEventListener('bw:auth-expired', handleExpiredSession);
    return () => window.removeEventListener('bw:auth-expired', handleExpiredSession);
  }, []);

  useEffect(() => {
    const refreshStorePermissions = async () => {
      const requestId = ++scopePermissionRequestId.current;
      try {
        const functions = await getFunctions();
        if (requestId !== scopePermissionRequestId.current) return;
        setUser((currentUser) => currentUser ? { ...currentUser, functions } : currentUser);
      } catch (refreshError) {
        if (requestId !== scopePermissionRequestId.current || refreshError?.status === 401) return;
        notification.error(getErrorMessage(refreshError));
      }
    };

    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, refreshStorePermissions);
    return () => {
      scopePermissionRequestId.current += 1;
      window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, refreshStorePermissions);
    };
  }, []);

  const initData = async () => {
    setInitializing(true);
    setError(null);
    try {
      const profileResponse = await getProfile();
      await ensureAuthenticatedPortalScope();
      const functions = await getFunctions();
      let profileData = normalizeAuthUser(profileResponse);
      profileData.functions = functions;
      setUser(profileData);
    } catch (error) {
      if (error.status === 401) {
        setUser(null);
      } else {
        setError(error);
        notification.error(getErrorMessage(error));
      }
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    initData();
  }, []);
  const logout = async () => {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_LOGOUT_EVENT));
    try {
      try {
        const notificationDeviceId = window.localStorage.getItem(NOTIFICATION_DEVICE_STORAGE_KEY);
        if (notificationDeviceId) await unregisterNotificationDevice(notificationDeviceId).catch(() => {});
      } catch (_) { /* Browser storage must not prevent signing out. */ }
      await logoutRequest();
      setUser(null);
    } catch (error) {
      if (error?.status === 401) {
        // The API has already confirmed that the server-side session ended.
        setUser(null);
        return;
      }

      // A network/DB/Redis outage does not prove that the HttpOnly cookies were
      // revoked. Keep the signed-in screen so the user can retry a real logout.
      window.dispatchEvent(new CustomEvent(NOTIFICATION_LOGOUT_CANCELLED_EVENT));
      notification.error(getErrorMessage(error));
    }
  };

  const setSetting = useCallback((nextValue) => {
    setSettingState((currentValue) => {
      const resolvedValue =
        typeof nextValue === 'function'
          ? nextValue(currentValue)
          : {
              ...currentValue,
              ...(nextValue || {}),
            };

      const nextSetting = {
        ...currentValue,
        ...(resolvedValue || {}),
      };

      const normalizedNavigationLayout = normalizeNavigationLayout(nextSetting?.navigationLayout);
      nextSetting.navigationLayout = normalizedNavigationLayout;
      persistNavigationLayout(normalizedNavigationLayout);

      return nextSetting;
    });
  }, []);

  const value = {
    user,
    permissionSnapshot,
    setUser,
    retryInitialization: initData,
    initializing,
    setInitializing,
    error,
    setError,
    logout,
    setting,
    setSetting,
    collapsed,
    setCollapsed,
    isUserShift,
    setIsUserShift
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
